import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_POLLS = 5;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const { t } = useLanguage();
  const ps = t.paymentSuccess;
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("pending");
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/api/payments/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          setStatus("error");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === "pending" && (
          <div data-testid="payment-status-pending">
            <Loader2 className="w-10 h-10 text-brand-blue animate-spin mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold mb-2">{ps.pendingTitle}</h1>
            <p className="text-neutral-400 text-sm">{ps.pendingSubtitle}</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-status-paid">
            <CheckCircle2 className="w-14 h-14 text-brand-blue mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-bold mb-3">{ps.title}</h1>
            <p className="text-neutral-400 text-base mb-8">{ps.subtitle}</p>
          </div>
        )}
        {status === "error" && (
          <div data-testid="payment-status-error">
            <XCircle className="w-14 h-14 text-brand-red mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold mb-3">{ps.errorTitle}</h1>
            <p className="text-neutral-400 text-sm mb-8">{ps.errorSubtitle}</p>
          </div>
        )}
        <Link
          to="/"
          data-testid="payment-back-home-button"
          className="inline-flex items-center gap-2 text-sm font-semibold text-white px-6 py-3 rounded-full border border-white/15 hover:bg-white/[0.06] transition-all duration-300"
        >
          <ArrowLeft className="w-4 h-4" /> {ps.backHomeButton}
        </Link>
      </div>
    </div>
  );
}
